import { Box, Button, CircularProgress } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import telegramIcon from "~/assets/icons/telegram.svg";
import { safeRedirectPath } from "~/utils/safeRedirectPath";
import { supabase } from "~/utils/supabase.client";

const POLL_INTERVAL_MS = 1500;
const MAX_POLL_DURATION_MS = 5 * 60 * 1000;
const NONCE_FETCH_RETRY_LIMIT = 3;
const SESSION_STORAGE_PREFIX = "telegram-login-secret:";

interface NonceCreateResponse {
  ok: boolean;
  nonce?: string;
  clientSecret?: string;
  expiresAt?: string;
  deepLink?: string;
}

interface NonceConsumeResponse {
  ok: boolean;
  isNewUser?: boolean;
  email?: string;
  token?: string;
  error?: string;
}

interface NoncePollResponse {
  ok: boolean;
  status?: "pending" | "ready" | "expired";
}

export type TelegramLoginProgress = "idle" | "polling" | "consuming";

interface TelegramDeepLinkLoginButtonProps {
  /**
   * Same-origin path the user should land on after the session is established.
   * New users always get sent to /signup regardless (matches the legacy widget
   * flow), and falls back to /select-team for returning users.
   */
  next: string | null;
  label: string;
  onError: (key: string, severity?: "error" | "warning") => void;
  onProgress?: (state: TelegramLoginProgress) => void;
}

function backendBase(): string {
  if (typeof window !== "undefined") {
    return (window.__ENV__?.BACKEND_URL ?? "").replace(/\/$/, "");
  }
  return "";
}

/**
 * Bot-deep-link Telegram login button. Replaces the official Login Widget,
 * which is unusable inside the Telegram in-app browser (Telegram intercepts
 * `t.me`/`oauth.telegram.org` URLs and severs the widget's callback channel).
 *
 * Flow:
 *   1. On mount, request a nonce + clientSecret from the backend. The secret
 *      lives in component state AND sessionStorage (keyed by nonce) so it
 *      survives the user's Telegram round-trip even if React re-mounts.
 *   2. On click, navigate the browser to `t.me/<bot>?start=login_<nonce>`.
 *      Telegram either opens its native client (in-app browser, mobile) or
 *      its web app (desktop) — either way the user lands in the bot chat.
 *   3. Concurrently, poll the backend every 1.5s. When status flips to
 *      `ready`, call /consume with the saved clientSecret to receive a
 *      Supabase OTP, redeem it client-side, then full-reload to `next` so
 *      Remix loaders pick up the new session cookies.
 */
export function TelegramDeepLinkLoginButton({
  next,
  label,
  onError,
  onProgress,
}: TelegramDeepLinkLoginButtonProps) {
  const [nonce, setNonce] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [deepLink, setDeepLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Bumping `nonceVersion` re-runs the effect that fetches a new nonce.
  // Used after expiry so the button is immediately usable on the next click.
  const [nonceVersion, setNonceVersion] = useState(0);
  const consumingRef = useRef(false);
  const pollStartedAtRef = useRef<number | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const failureCountRef = useRef(0);

  // Mount: fetch a nonce. Re-runs when `nonceVersion` bumps (post-expiry).
  useEffect(() => {
    let cancelled = false;
    setNonce(null);
    setClientSecret(null);
    setDeepLink(null);

    // The backend persists `next` on the nonce row so the bot's post-claim
    // ticket can carry it through the in-app browser hop. Re-validated on
    // the server.
    const safeNext = safeRedirectPath(next);
    void (async () => {
      try {
        const resp = await fetch(`${backendBase()}/api/auth/telegram/login-nonce`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(safeNext ? { next: safeNext } : {}),
        });
        if (!resp.ok) throw new Error(`status_${resp.status}`);
        const data = (await resp.json()) as NonceCreateResponse;
        if (cancelled) return;
        if (!data.ok || !data.nonce || !data.clientSecret || !data.deepLink) {
          throw new Error("malformed_response");
        }
        try {
          sessionStorage.setItem(
            `${SESSION_STORAGE_PREFIX}${data.nonce}`,
            data.clientSecret,
          );
        } catch {
          // sessionStorage unavailable — secret stays in component state only.
          // The user can still complete sign-in within this React mount.
        }
        setNonce(data.nonce);
        setClientSecret(data.clientSecret);
        setDeepLink(data.deepLink);
      } catch {
        if (cancelled) return;
        onError("auth.login.errors.telegramNonceFailed");
      }
    })();

    return () => {
      cancelled = true;
    };
    // `onError` is referenced for stability — caller should pass a stable
    // reference. Re-running on identity change would tear down the polling
    // unnecessarily.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonceVersion, next]);

  // Cleanup any active poll on unmount + on nonce reset.
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, []);

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    pollStartedAtRef.current = null;
    failureCountRef.current = 0;
    onProgress?.("idle");
  };

  const handleExpired = () => {
    stopPolling();
    setBusy(false);
    onError("auth.login.errors.telegramExpired", "warning");
    if (nonce) {
      try {
        sessionStorage.removeItem(`${SESSION_STORAGE_PREFIX}${nonce}`);
      } catch {
        // sessionStorage may be blocked.
      }
    }
    setNonceVersion((v) => v + 1);
  };

  const consumeAndRedirect = async () => {
    if (consumingRef.current) return;
    if (!nonce) return;
    const secret =
      clientSecret ??
      (() => {
        try {
          return sessionStorage.getItem(`${SESSION_STORAGE_PREFIX}${nonce}`);
        } catch {
          return null;
        }
      })();
    if (!secret) {
      handleExpired();
      return;
    }
    consumingRef.current = true;
    onProgress?.("consuming");
    try {
      const resp = await fetch(
        `${backendBase()}/api/auth/telegram/login-nonce/${encodeURIComponent(nonce)}/consume`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ clientSecret: secret }),
        },
      );
      const data = (await resp.json().catch(() => ({}))) as NonceConsumeResponse;
      if (!resp.ok || !data.ok || !data.email || !data.token) {
        // 409 not_ready can happen if the polling SELECT and the consume race
        // before the bot's UPDATE hits Postgres replication. Stay in the loop
        // and try again on the next tick.
        if (data.error === "not_ready") {
          consumingRef.current = false;
          return;
        }
        if (
          data.error === "expired" ||
          data.error === "already_consumed" ||
          data.error === "invalid_secret"
        ) {
          handleExpired();
          return;
        }
        onError("auth.login.errors.telegramFailed");
        stopPolling();
        setBusy(false);
        return;
      }

      const { error } = await supabase.auth.verifyOtp({
        type: "email",
        email: data.email,
        token: data.token,
      });
      if (error) {
        onError("auth.login.errors.telegramFailed");
        stopPolling();
        setBusy(false);
        return;
      }

      try {
        sessionStorage.removeItem(`${SESSION_STORAGE_PREFIX}${nonce}`);
      } catch {
        // sessionStorage may be blocked.
      }
      stopPolling();
      // New users always go to /signup (ToS) regardless of `next`, matching
      // the legacy widget callback (`api.auth.telegram.callback.tsx`).
      const destination = data.isNewUser
        ? "/signup"
        : (safeRedirectPath(next) ?? "/select-team");
      window.location.replace(destination);
    } catch {
      onError("auth.login.errors.telegramFailed");
      stopPolling();
      setBusy(false);
    } finally {
      consumingRef.current = false;
    }
  };

  const pollOnce = async () => {
    if (!nonce) return;
    if (
      pollStartedAtRef.current !== null &&
      Date.now() - pollStartedAtRef.current > MAX_POLL_DURATION_MS
    ) {
      handleExpired();
      return;
    }
    try {
      const resp = await fetch(
        `${backendBase()}/api/auth/telegram/login-nonce/${encodeURIComponent(nonce)}`,
      );
      if (!resp.ok) {
        failureCountRef.current += 1;
        if (failureCountRef.current >= NONCE_FETCH_RETRY_LIMIT) {
          stopPolling();
          setBusy(false);
          onError("auth.login.errors.telegramNonceFailed");
        }
        return;
      }
      failureCountRef.current = 0;
      const data = (await resp.json()) as NoncePollResponse;
      if (!data.ok || data.status === "expired") {
        handleExpired();
        return;
      }
      if (data.status === "ready") {
        await consumeAndRedirect();
      }
    } catch {
      failureCountRef.current += 1;
      if (failureCountRef.current >= NONCE_FETCH_RETRY_LIMIT) {
        stopPolling();
        setBusy(false);
        onError("auth.login.errors.telegramNonceFailed");
      }
    }
  };

  // Hold the latest `pollOnce` in a ref so the once-registered visibility
  // listener and any pending setInterval tick always invoke the freshest
  // closure (which captures the current `nonce` / `clientSecret` from
  // component state). Without this, the listener registered on mount would
  // forever call the very first render's pollOnce — back when nonce was
  // still null, so the poll was a silent no-op.
  const pollOnceRef = useRef<() => Promise<void>>(async () => undefined);
  pollOnceRef.current = pollOnce;

  const startPolling = () => {
    if (pollTimerRef.current) return;
    pollStartedAtRef.current = Date.now();
    failureCountRef.current = 0;
    void pollOnceRef.current();
    pollTimerRef.current = setInterval(() => {
      void pollOnceRef.current();
    }, POLL_INTERVAL_MS);
  };

  // Visibility change: when the tab regains focus (user returned from the
  // Telegram app), poll immediately so the redirect feels instant instead of
  // waiting up to POLL_INTERVAL_MS for the next tick.
  useEffect(() => {
    const onVisibility = () => {
      if (
        document.visibilityState === "visible" &&
        pollTimerRef.current !== null
      ) {
        void pollOnceRef.current();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const handleClick = () => {
    if (!deepLink) return;
    setBusy(true);
    onProgress?.("polling");
    startPolling();
    // Use href (not open) so Telegram apps that intercept `t.me` URLs treat
    // this as a top-level navigation and open the bot chat in their own UI.
    window.location.href = deepLink;
  };

  const ready = nonce !== null && deepLink !== null;

  return (
    <Button
      variant="outlined"
      color="primary"
      fullWidth
      disabled={!ready || busy}
      startIcon={
        busy ? (
          <CircularProgress size={20} thickness={4.5} color="primary" />
        ) : (
          <Box
            component="img"
            src={telegramIcon}
            alt=""
            sx={{ width: 24, height: 24 }}
          />
        )
      }
      onClick={handleClick}
      sx={{ minHeight: 52, borderRadius: "8px" }}
    >
      {label}
    </Button>
  );
}
